import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useGetApiOrdersSearch } from '@iplan/api-client/internal';
import { Search, X } from 'lucide-react';

/**
 * Global order search in the top bar — ported from Web.React's GlobalSearch,
 * wired to the generated `useGetApiOrdersSearch` endpoint. Debounced
 * input + a results dropdown; selecting a result opens that order's form via
 * the existing `events/:eventId/orders/:orderId` route (same route-param
 * pattern OrdersPage already consumes for legacy deep links — see
 * OrdersPage.tsx's `routeEventId`/`routeOrderId` handling).
 *
 * The search endpoint is typed `void` in the OpenAPI spec (OData projection),
 * so the row shape is declared locally. ⌘K / Ctrl-K focuses the field.
 */
interface OrderSearchResult {
  id: string;
  orderNumber?: string | null;
  orderDescription?: string | null;
  equipmentNumber?: string | null;
  eventId?: string | null;
  eventName?: string | null;
  approvalStatus?: string | null;
}

export function GlobalSearch(): ReactNode {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  // Debounce the query (300ms, matches the legacy GlobalSearch).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), 300);
    return () => clearTimeout(t);
  }, [value]);

  // ⌘K / Ctrl-K focuses the search field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const query = useGetApiOrdersSearch(
    { searchQuery: debounced },
    { query: { enabled: debounced.length > 1 } },
  );
  const results = ((query.data as unknown as OrderSearchResult[] | undefined) ?? []).slice(0, 25);

  const openResult = (row: OrderSearchResult): void => {
    setOpen(false);
    setValue('');
    // Route via events/:eventId/orders/:orderId — the same URL shape
    // OrdersPage already reads to switch Event context and open the order
    // form sidecar (see OrdersPage.tsx). If the search row is missing its
    // eventId (shouldn't happen — the search DTO always projects it — but
    // don't crash), fall back to the plain orders list rather than
    // navigating to an order-scoped route with no event.
    if (row.eventId) {
      navigate(`/events/${row.eventId}/orders/${row.id}`);
    } else {
      navigate('/orders');
    }
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-xs sm:w-72">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        placeholder="Search orders…"
        aria-label="Search orders"
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => debounced.length > 1 && setOpen(true)}
        className="h-9 w-full rounded-md border border-border bg-background pl-9 pr-12 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
      />
      <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 select-none rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-block">
        ⌘K
      </kbd>

      {open && debounced.length > 1 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-auto rounded-md border border-border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-medium text-muted-foreground">
              {query.isFetching ? 'Searching…' : `${results.length} result(s)`}
            </span>
            <button
              type="button"
              aria-label="Close search results"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setOpen(false)}
            >
              <X className="size-3.5" />
            </button>
          </div>
          {results.length === 0 && !query.isFetching ? (
            <div className="px-3 py-4 text-sm text-muted-foreground">No orders found.</div>
          ) : (
            <ul className="divide-y divide-border">
              {results.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => openResult(row)}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <span className="font-medium text-foreground">
                      {row.orderNumber ?? '(no number)'}
                      {row.equipmentNumber ? ` · ${row.equipmentNumber}` : ''}
                    </span>
                    {row.orderDescription && (
                      <span className="truncate text-xs text-muted-foreground">
                        {row.orderDescription}
                      </span>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      {[row.eventName, row.approvalStatus].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
